import { useState, useEffect, useRef, useCallback } from 'react';
import { Head } from '@inertiajs/react';
import { QRCodeSVG } from 'qrcode.react';
import { MainLayout } from '../../layouts/MainLayout';
import { formatPrice } from '../../utils/formatPrice';
import { route } from '../../utils/route';

interface MercadoPagoQRProps {
  order: {
    id: number;
    formatted_number: string;
    total: number;
  };
  qrData: string;
  mpOrderId: string;
  initPoint: string;
}

const EXPIRATION_SECONDS = 15 * 60; // 15 minutes

export default function MercadoPagoQR({ order, qrData, mpOrderId, initPoint }: MercadoPagoQRProps) {
  const [secondsLeft, setSecondsLeft] = useState(EXPIRATION_SECONDS);
  const [paid, setPaid] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    // Countdown timer
    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          stopPolling();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Poll payment status
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(route('orders.qr_status', { id: order.id }));
        if (res.ok) {
          const data = await res.json();
          if (data.paid) {
            setPaid(true);
            stopPolling();
          }
        }
      } catch {
        // ignore fetch errors, will retry
      }
    }, 3000);

    return stopPolling;
  }, [order.id, stopPolling]);

  // Redirect after payment detected
  useEffect(() => {
    if (paid) {
      const timeout = setTimeout(() => {
        window.location.href = route('orders.fin');
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [paid]);

  const expired = secondsLeft <= 0;
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return (
    <MainLayout>
      <Head title="Pagar con QR">
        <meta name="robots" content="noindex" />
      </Head>

      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Escaneá el QR para pagar</h1>
          <p className="text-gray-600 mt-1">
            Pedido {order.formatted_number} &middot; {formatPrice(order.total)}
          </p>
        </div>

        {/* QR Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          {paid ? (
            <div className="py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xl font-semibold text-green-700">Pago recibido</p>
              <p className="text-gray-500 mt-2">Redirigiendo...</p>
            </div>
          ) : expired ? (
            <div className="py-8">
              <p className="text-lg font-medium text-gray-700 mb-4">El QR expiró</p>
              <a
                href={route('orders.pay', { id: order.id }) + '?method=1'}
                className="inline-block bg-blue-600 text-white font-medium px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Generar nuevo QR
              </a>
            </div>
          ) : (
            <>
              <div className="inline-block p-4 bg-white rounded-xl">
                <QRCodeSVG
                  value={qrData}
                  size={240}
                  level="M"
                  includeMargin={false}
                />
              </div>

              {/* Timer */}
              <p className="text-sm text-gray-500 mt-4">
                Expira en {minutes}:{seconds.toString().padStart(2, '0')}
              </p>

              {/* Instructions */}
              <div className="mt-6 text-left text-sm text-gray-600 space-y-2">
                <p className="flex items-start gap-2">
                  <span className="font-semibold text-gray-800 shrink-0">1.</span>
                  Abrí la app de Mercado Pago u otra billetera digital en tu celular
                </p>
                <p className="flex items-start gap-2">
                  <span className="font-semibold text-gray-800 shrink-0">2.</span>
                  Escaneá este código QR
                </p>
                <p className="flex items-start gap-2">
                  <span className="font-semibold text-gray-800 shrink-0">3.</span>
                  Confirmá el pago en tu celular
                </p>
              </div>
            </>
          )}
        </div>

        {/* Fallback link */}
        {!paid && !expired && (
          <div className="text-center mt-6">
            <a
              href={initPoint}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Prefiero pagar en este navegador
            </a>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
